import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions, DynamicAddressList, type AddressItem } from '@repo/web-form';

import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { companyBaseSchema, companySchema, STATE_CODES } from '../utils/validation';
import { useCreateCompany, useUpdateCompany, type Company } from '../network/org.api';
import { useToast } from '../../../components/ui/Toast';

interface CompanySetupFormProps {
  initialData?: Company | null;
}

const STATE_OPTIONS = Object.keys(STATE_CODES).map((code) => ({
  value: code,
  label: code, 
})).sort((a, b) => a.label.localeCompare(b.label));

const companyShape = companyBaseSchema.shape;

const validate = (schema: import('zod').ZodTypeAny, value: unknown) => {
  const res = schema.safeParse(value);
  return res.success ? undefined : res.error.issues[0].message;
};

const companySetupFormConfig = defineFormPage({
  resource: 'company-setup',
  defaultValues: {
    name: '',
    legalName: '',
    cin: '',
    gstin: '',
    pan: '',
    registeredAddress: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
    },
    stateCode: '',
    rocNumber: '',
    foremanName: '',
  },
  schema: companySchema,
});

export function CompanySetupForm({ initialData }: CompanySetupFormProps) {
  const navigate = useNavigate();
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const { toast } = useToast();

  const [companyAddresses, setCompanyAddresses] = useState<AddressItem[]>([
    {
      addressType: 'OFFICE',
      label: 'REGISTERED_OFFICE',
      addressLine1: initialData?.registeredAddress?.line1 || '',
      addressLine2: initialData?.registeredAddress?.line2 || '',
      city: initialData?.registeredAddress?.city || '',
      stateCode: initialData?.stateCode || 'TN',
      pincode: initialData?.registeredAddress?.pincode || '',
      isPrimary: true,
    },
  ]);

  const { form, isSubmitting, fieldError: fe } = useFormPage(
    { ...companySetupFormConfig, mode: initialData ? 'edit' : 'create' },
    { mutateAsync: async (v) => v },
    initialData ? { initialValues: initialData } : undefined,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
    if (!form.state.canSubmit) return;
    try {
      const payload = {
        ...form.state.values,
        companyAddresses,
      };
      if (initialData) {
        await updateMutation.mutateAsync({ id: initialData.id, body: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      void navigate({ to: '/settings/branches' });
    } catch (err: unknown) {
      console.error('Failed to save company', err);
      const msg = err instanceof Error ? err.message : 'Failed to save company';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-2xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form.Field
          name="name"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.name, value) }}
          children={(field: any) => (
            <FormField
              label="Operating Name"
              required
              error={fe(field.state.meta)}
            >
              <Input
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                placeholder="e.g. Kraphene Chits Chennai"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="legalName"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.legalName, value) }}
          children={(field: any) => (
            <FormField
              label="Legal Name (as per ROC)"
              required
              error={fe(field.state.meta)}
            >
              <Input
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                placeholder="e.g. Kraphene Financial Services Pvt Ltd"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="pan"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.pan, value) }}
          children={(field: any) => (
            <FormField
              label="PAN"
              required
              error={fe(field.state.meta)}
              hint="Format: 5 alpha + 4 numeric + 1 alpha"
            >
              <Input
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F"
                maxLength={10}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="stateCode"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.stateCode, value) }}
          children={(field: any) => (
            <FormSelect
              label="Base State (for GST)"
              required
              options={[{ value: '', label: 'Select State' }, ...STATE_OPTIONS]}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.handleChange(e.target.value)}
              error={fe(field.state.meta)}
              disabled={isSubmitting}
            />
          )}
        />

        <form.Field
          name="gstin"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.gstin, value) }}
          children={(field: any) => (
            <FormField
              label="GSTIN"
              error={fe(field.state.meta)}
              hint="15-character format; must match base state prefix"
            >
              <Input
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.toUpperCase())}
                placeholder="33ABCDE1234F1Z1"
                maxLength={15}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="cin"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.cin, value) }}
          children={(field: any) => (
            <FormField
              label="CIN"
              error={fe(field.state.meta)}
            >
              <Input
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.toUpperCase())}
                placeholder="U12345TN2024PTC123456"
                maxLength={21}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />
      </div>

      {/* 1-to-Many Company Address Management */}
      <div className="border-t pt-6">
        <DynamicAddressList
          addresses={companyAddresses}
          disabled={isSubmitting}
          onChange={(newAddrs) => {
            setCompanyAddresses(newAddrs);
            const primary = newAddrs.find((a) => a.isPrimary) || newAddrs[0];
            if (primary) {
              form.setFieldValue('registeredAddress', {
                line1: primary.addressLine1,
                line2: primary.addressLine2 || '',
                city: primary.city,
                state: primary.stateCode,
                pincode: primary.pincode,
              });
              form.setFieldValue('stateCode', primary.stateCode);
            }
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
        <form.Field
          name="rocNumber"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.rocNumber, value) }}
          children={(field: any) => (
            <FormField
              label="ROC Registration Number"
              error={fe(field.state.meta)}
            >
              <Input
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="foremanName"
          validators={{ onChange: ({ value }: { value: string }) => validate(companyShape.foremanName, value) }}
          children={(field: any) => (
            <FormField
              label="Foreman / Chief Officer Name"
              required
              error={fe(field.state.meta)}
            >
              <Input
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />
      </div>

      <FormActions
        isSubmitting={isSubmitting}
        submitLabel={initialData ? 'Update Company' : 'Complete Setup'}
        showCancel={false}
      />
    </form>
  );
}